<template>
  <div class="w-full">
    <div :class="inline ? 'flex items-center gap-3 flex-nowrap' : ''">
      <!-- Optional internal label (use only when not providing an external label) -->
      <label
        v-if="label"
        :for="resolvedId"
        :class="inline
          ? ['text-sm font-medium text-gray-700 mb-0', labelWidth, 'whitespace-nowrap']
          : 'block text-sm font-medium text-gray-700 mb-1'"
      >
        {{ label }}
      </label>

      <!-- Control cell -->
      <div :class="inline ? 'flex-1 min-w-0' : ''">
        <div class="relative">
          <select
            :id="resolvedId"
            :name="name || undefined"
            :value="modelValue"
            :disabled="disabled"
            :aria-invalid="error ? 'true' : 'false'"
            class="block w-full h-10 rounded-lg border border-gray-300 bg-white px-3
                   text-sm placeholder:text-gray-400 shadow-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                   disabled:bg-gray-100 disabled:text-gray-500"
            @change="onChange"
            @blur="$emit('blur', $event)"
            @focus="$emit('focus', $event)"
          >
            <option v-if="placeholder" disabled hidden value="">
              {{ placeholder }}
            </option>
            <option v-for="opt in normalizedOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <p v-if="hint && !error" class="mt-1 text-xs text-gray-500">{{ hint }}</p>
        <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'

export default {
  name: 'SelectState',
  inheritAttrs: false,
  props: {
    // IDENTIFIERS
    id:          { type: String, default: null },
    name:        { type: String, default: '' },

    // VALUE
    modelValue:  { type: [String, Number], default: '' },

    // UI
    label:       { type: String, default: '' },
    placeholder: { type: String, default: '' },
    hint:        { type: String, default: '' },
    error:       { type: String, default: '' },
    disabled:    { type: Boolean, default: false },

    // DATA
    options:     { type: Array, default: () => [] },

    // BEHAVIOR
    autoSelectFirst: { type: Boolean, default: false },

    // LAYOUT
    inline:      { type: Boolean, default: false },
    labelWidth:  { type: String,  default: 'w-40' },
  },
  emits: ['update:modelValue', 'blur', 'focus'],

  setup(props, { emit }) {
    const uid = `select-state-${Math.random().toString(36).slice(2, 8)}`
    const resolvedId = computed(() => props.id || uid)

    const normalizedOptions = computed(() => {
      const src = props.options || []
      if (!src.length) return []
      const first = src[0]
      if (typeof first === 'string' || typeof first === 'number') {
        return src.map(v => ({ label: String(v), value: String(v) }))
      }
      return src.map(o => ({
        label: 'label' in o ? String(o.label) : String(o.value ?? ''),
        value: 'value' in o ? String(o.value) : String(o.label ?? ''),
      }))
    })

    function onChange(e) {
      emit('update:modelValue', e.target.value)
    }

    onMounted(() => {
      if (props.autoSelectFirst &&
          !props.modelValue &&
          normalizedOptions.value.length > 0) {
        emit('update:modelValue', normalizedOptions.value[0].value)
      }
    })

    return { resolvedId, normalizedOptions, onChange }
  },
}
</script>

<style scoped>
/* Hard-stop any rogue global rule like svg { width: Xvh } from affecting selects */
:where(select) { background-repeat: no-repeat; }
</style>
